import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, CheckCircle2, Search, ChevronDown, BadgeCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`;

const Chips = ({ options, value, max, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map((o) => {
      const on = value.includes(o);
      const toggle = () => {
        if (on) onChange(value.filter((x) => x !== o));
        else if (value.length < max) onChange([...value, o]);
        else toast.error(`Max ${max} allowed`);
      };
      return (
        <button key={o} onClick={toggle} type="button"
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#00A79D]'}`}>
          {o}
        </button>
      );
    })}
  </div>
);

const SupplierVerification = () => {
  const [rows, setRows] = useState([]);
  const [tax, setTax] = useState({ care_settings: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [alloc, setAlloc] = useState({}); // key `${cid}:${slug}` -> {care_settings, categories}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/supplier-verify/overview`, { params: q ? { q } : {} });
      setRows(data.suppliers || []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { axios.get(`${API}/admin/supplier-verify/taxonomy`).then((r) => setTax(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (cid, on) => {
    try {
      await axios.post(`${API}/admin/supplier-verify/${cid}/${on ? 'confirm' : 'unconfirm'}`);
      toast.success(on ? 'Marked as confirmed supplier' : 'Confirmation removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const key = (cid, slug) => `${cid}:${slug}`;
  const getAlloc = (cid, p) => alloc[key(cid, p.slug)] || { care_settings: p.care_settings || [], categories: p.categories || [] };
  const setA = (cid, slug, patch) => setAlloc((a) => ({ ...a, [key(cid, slug)]: { ...getAlloc(cid, { slug }), ...patch } }));
  const saveAlloc = async (cid, p) => {
    const a = getAlloc(cid, p);
    try {
      await axios.put(`${API}/admin/supplier-verify/${cid}/product-allocation`, { product_slug: p.slug, care_settings: a.care_settings, categories: a.categories });
      toast.success(`Saved allocation for ${p.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [research, setResearch] = useState({});
  const rKey = (cid, slug) => `${cid}:${slug}`;
  const getR = (cid, p, saved) => research[rKey(cid, p.slug)] || saved || {};
  const setR = (cid, slug, patch, saved) => setResearch((r) => ({ ...r, [rKey(cid, slug)]: { ...(r[rKey(cid, slug)] || saved || {}), ...patch } }));
  const saveResearch = async (cid, p, saved) => {
    const r = getR(cid, p, saved);
    try { await axios.put(`${API}/admin/supplier-verify/${cid}/research`, { product_slug: p.slug, summary: r.summary || '', keywords: r.keywords || '', competitors: r.competitors || '', messaging: r.messaging || '', report_url: r.report_url || '' }); toast.success(`Research saved for ${p.name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const invitePartner = async (cid) => {
    try { await axios.post(`${API}/admin/supplier-verify/${cid}/partner-invite`); toast.success('Invited to partner programme'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const [aiBusy, setAiBusy] = useState('');
  const autoDraftResearch = async (cid, slug) => {
    setAiBusy(rKey(cid, slug));
    try { const { data } = await axios.post(`${API}/admin/supplier-verify/${cid}/research/auto-draft`, { product_slug: slug }); setR(cid, slug, data.draft); toast.success('AI draft ready - review & save'); }
    catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); } finally { setAiBusy(''); }
  };
  const downloadBlob = async (url, filename) => {
    try { const res = await axios.get(url, { responseType: 'blob' }); const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = filename; a.click(); }
    catch { toast.error('Download failed'); }
  };
  const bulkDraft = async (cid) => {
    setAiBusy(`bulk:${cid}`);
    try { const { data } = await axios.post(`${API}/admin/supplier-verify/${cid}/research/auto-draft-all`); toast.success(`Drafted research for ${data.drafted} product(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setAiBusy(''); }
  };
  const [pq, setPq] = useState({});
  const getPq = (cid, s) => pq[cid] || s.partner_quote || {};
  const setPqF = (cid, patch) => setPq((p) => ({ ...p, [cid]: { ...(p[cid] || rows.find((x) => x.company_id === cid)?.partner_quote || {}), ...patch } }));
  const draftPartnerQuote = async (cid) => {
    try { const { data } = await axios.post(`${API}/admin/supplier-verify/${cid}/partner-quote/draft`); setPqF(cid, data.draft); toast.success('Draft quote built from questionnaire'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const savePartnerQuote = async (cid) => {
    const q = getPq(cid, rows.find((x) => x.company_id === cid) || {});
    try { await axios.put(`${API}/admin/supplier-verify/${cid}/partner-quote`, { monthly_gross: Number(q.monthly_gross) || 0, setup_gross: Number(q.setup_gross) || 0, scope: q.scope || '', notes: q.notes || '' }); toast.success('Partner quote saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const sendPartnerQuote = async (cid) => {
    try { await axios.post(`${API}/admin/supplier-verify/${cid}/partner-quote/send`); toast.success('Partner quote sent to supplier'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="supplier-verification-page">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-[#00A79D]" /><h1 className="font-heading text-2xl font-bold text-gray-900">Supplier verification</h1></div>
      <p className="text-sm text-gray-500 mb-5">Track data-check progress, auto-quotes, and allocate care settings + categories to confirmed suppliers.</p>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all suppliers by name…" data-testid="verify-search"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm" />
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> : (
        <div className="space-y-2">
          {rows.length === 0 && <p className="text-sm text-gray-400">No suppliers yet. Search by name to find any supplier.</p>}
          {rows.map((s) => {
            const isOpen = open === s.company_id;
            return (
              <div key={s.company_id} className="border border-gray-200 rounded-xl bg-white" data-testid={`verify-row-${s.slug}`}>
                <button onClick={() => setOpen(isOpen ? null : s.company_id)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                  <div className="flex items-center gap-3">
                    {s.verified ? <CheckCircle2 className="w-5 h-5 text-[#00A79D]" /> : <span className="text-xs font-semibold text-gray-400 w-5 text-center">{s.sections_confirmed}/{s.sections_total}</span>}
                    <div>
                      <span className="font-heading font-semibold text-gray-900 block">{s.name}</span>
                      <span className="text-xs text-gray-500">
                        {s.verified ? 'Data check complete' : `${s.sections_confirmed}/${s.sections_total} sections`}
                        {s.quote ? ` · Quote ${money(s.quote.monthly_gross)}/mo (${s.quote.status})` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.confirmed_supplier && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#00857D] bg-[#00A79D]/10 px-2 py-1 rounded-full"><BadgeCheck className="w-3.5 h-3.5" /> Confirmed</span>}
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => confirm(s.company_id, !s.confirmed_supplier)} disabled={!s.verified}
                        data-testid={`verify-confirm-${s.slug}`}
                        className={`text-sm font-semibold px-4 py-2 rounded-lg ${s.confirmed_supplier ? 'bg-gray-100 text-gray-700' : 'bg-[#00A79D] text-white disabled:opacity-50'}`}>
                        {s.confirmed_supplier ? 'Remove confirmed status' : 'Mark as confirmed supplier'}
                      </button>
                      {!s.verified && <span className="text-xs text-amber-600">Supplier must complete the data check first.</span>}
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Per-product allocation (up to 3 each)</p>
                        {(s.products || []).length > 1 && (
                          <button onClick={() => bulkDraft(s.company_id)} disabled={aiBusy === `bulk:${s.company_id}`} data-testid={`verify-bulk-ai-${s.slug}`}
                            className="text-xs font-semibold text-[#1A3A5F] bg-[#EAF6FB] px-3 py-1.5 rounded-md disabled:opacity-60">{aiBusy === `bulk:${s.company_id}` ? 'Drafting all…' : '✨ Draft research for all products'}</button>
                        )}
                      </div>
                      {(s.products || []).length === 0 && <p className="text-sm text-gray-400">No products on this listing.</p>}
                      {(s.products || []).map((p) => {
                        const a = getAlloc(s.company_id, p);
                        const saved = (s.product_research || {})[p.slug] || {};
                        const rv = getR(s.company_id, p, saved);
                        const aik = `${s.company_id}:${p.slug}`;
                        return (
                          <div key={p.slug} className="border border-gray-200 rounded-lg p-4" data-testid={`verify-product-${p.slug}`}>
                            <p className="font-semibold text-gray-800 text-sm mb-2">{p.name}</p>
                            <p className="text-xs text-gray-500 mb-1">Care settings ({a.care_settings.length}/3)</p>
                            <Chips options={tax.care_settings} value={a.care_settings} max={3} onChange={(v) => setA(s.company_id, p.slug, { care_settings: v })} testid={`verify-settings-${p.slug}`} />
                            <p className="text-xs text-gray-500 mb-1 mt-3">Categories ({a.categories.length}/3)</p>
                            <Chips options={tax.categories} value={a.categories} max={3} onChange={(v) => setA(s.company_id, p.slug, { categories: v })} testid={`verify-cats-${p.slug}`} />
                            <button onClick={() => saveAlloc(s.company_id, p)} data-testid={`verify-save-${p.slug}`}
                              className="mt-3 text-sm font-semibold bg-[#1A3A5F] text-white px-4 py-2 rounded-lg">Save allocation</button>

                            <div className="mt-4 border-t border-gray-100 pt-3" data-testid={`verify-research-${p.slug}`}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Strategic research (visible to supplier)</p>
                                <button onClick={() => autoDraftResearch(s.company_id, p.slug)} disabled={aiBusy === aik} data-testid={`verify-research-ai-${p.slug}`}
                                  className="text-xs font-semibold text-[#1A3A5F] bg-[#EAF6FB] px-3 py-1.5 rounded-md disabled:opacity-60">{aiBusy === aik ? 'Drafting…' : '✨ Auto-draft with AI'}</button>
                              </div>
                              <div className="grid sm:grid-cols-2 gap-2">
                                {[['summary', 'Summary'], ['keywords', 'Target keywords'], ['competitors', 'Competitors'], ['messaging', 'Messaging'], ['report_url', 'Report URL']].map(([f, lbl]) => (
                                  <label key={f} className="block">
                                    <span className="text-[11px] text-gray-500">{lbl}</span>
                                    <input value={rv[f] || ''} onChange={(e) => setR(s.company_id, p.slug, { [f]: e.target.value }, saved)}
                                      data-testid={`verify-research-${f}-${p.slug}`} className="mt-0.5 w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm" />
                                  </label>
                                ))}
                              </div>
                              <button onClick={() => saveResearch(s.company_id, p, saved)} data-testid={`verify-research-save-${p.slug}`}
                                className="mt-2 text-sm font-semibold bg-[#00A79D] text-white px-4 py-2 rounded-lg">Save research</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {s.confirmed_supplier && (
                      <div className="border-t border-gray-100 pt-4 space-y-4">
                        {/* Partner programme (Part 2) */}
                        <div data-testid={`verify-partner-${s.slug}`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Partner programme</p>
                          {s.partner_invited
                            ? <span className="inline-flex items-center gap-1 text-sm text-[#00857D] font-semibold"><CheckCircle2 className="w-4 h-4" /> Invited</span>
                            : <button onClick={() => invitePartner(s.company_id)} data-testid={`verify-partner-invite-${s.slug}`} className="text-sm font-semibold bg-[#1A3A5F] text-white px-4 py-2 rounded-lg">Invite to partner</button>}
                          {s.partner_questionnaire && (
                            <div className="mt-3 bg-[#F9FAFB] rounded-lg p-3 space-y-1" data-testid={`verify-partner-answers-${s.slug}`}>
                              <p className="text-xs font-semibold text-gray-500">Questionnaire responses:</p>
                              {Object.entries(s.partner_questionnaire.answers || {}).map(([k, v]) => v ? <p key={k} className="text-xs text-gray-600"><span className="font-semibold">{k}:</span> {v}</p> : null)}
                            </div>
                          )}
                          {s.partner_questionnaire && (
                            <div className="mt-3 border border-gray-200 rounded-lg p-3" data-testid={`verify-partner-quote-${s.slug}`}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-gray-500">Partner-tier quote {getPq(s.company_id, s).status === 'sent' && <span className="text-[#00857D]">· sent</span>}</p>
                                <button onClick={() => draftPartnerQuote(s.company_id)} data-testid={`verify-partner-quote-draft-${s.slug}`} className="text-xs font-semibold text-[#00857D]">Auto-draft from answers</button>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <label className="block"><span className="text-[11px] text-gray-500">Monthly (incl VAT)</span>
                                  <input type="number" value={getPq(s.company_id, s).monthly_gross ?? ''} onChange={(e) => setPqF(s.company_id, { monthly_gross: e.target.value })} data-testid={`verify-partner-quote-monthly-${s.slug}`} className="mt-0.5 w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm" /></label>
                                <label className="block"><span className="text-[11px] text-gray-500">Setup (incl VAT)</span>
                                  <input type="number" value={getPq(s.company_id, s).setup_gross ?? ''} onChange={(e) => setPqF(s.company_id, { setup_gross: e.target.value })} className="mt-0.5 w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm" /></label>
                              </div>
                              <textarea value={getPq(s.company_id, s).scope || ''} onChange={(e) => setPqF(s.company_id, { scope: e.target.value })} rows={3} placeholder="Scope of work" className="mt-2 w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm" />
                              <div className="mt-2 flex gap-2">
                                <button onClick={() => savePartnerQuote(s.company_id)} data-testid={`verify-partner-quote-save-${s.slug}`} className="text-sm font-semibold bg-[#1A3A5F] text-white px-4 py-2 rounded-lg">Save quote</button>
                                <button onClick={() => sendPartnerQuote(s.company_id)} data-testid={`verify-partner-quote-send-${s.slug}`} className="text-sm font-semibold bg-[#00A79D] text-white px-4 py-2 rounded-lg">Send to supplier</button>
                                <button onClick={() => downloadBlob(`${API}/admin/supplier-verify/${s.company_id}/partner-quote/pdf`, `partner-proposal-${s.slug}.pdf`)} data-testid={`verify-partner-quote-pdf-${s.slug}`} className="text-sm font-semibold border border-gray-300 text-gray-700 px-4 py-2 rounded-lg">Download PDF</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupplierVerification;
